import React, { useEffect } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  useAccordionItemState,
  Button,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { MinusIcon } from "@root/assets/icons/MinusIcon";
import { PlusIcon } from "@root/assets/icons/PlusIcon";
import { DownloadIcon } from "@root/assets/icons/downloadIcon";
import { useFaqContext } from "@root/context/faq-context";
import useMediaQ from "@root/hooks/useMediaQ";
import { Paginate } from "@root/components/pagination";
import { RECORDS_PER_PAGE } from "@root/constants";
import { useTranslation } from "react-i18next";
import LinkifyText from "@root/components/common/LinkifyText";

const FaqAccordionIcon = () => {
  const { isOpen } = useAccordionItemState();
  const { sm } = useMediaQ();
  const isMobile = !sm;
  return isOpen ? (
    <MinusIcon />
  ) : (
    <PlusIcon color={isMobile ? "#1819B4" : "#2F2F34"} />
  );
};

const FaqTab: React.FC = () => {
  const {
    faqData,
    fetchFaqs,
    pageNo,
    setPageNo,
    pageCount,
    pageSize,
    downloadDocumentsZip,
    isDownloadingFaq,
  } = useFaqContext();
  const { sm } = useMediaQ();
  const { t } = useTranslation("common");
  const isMobile = !sm;

  useEffect(() => {
    const payload = {
      offset: (pageNo - 1) * pageSize,
      limit: pageSize,
      search: "",
    };
    fetchFaqs(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo]);

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
  };

  return (
    <Box w="100%">
      <Accordion allowToggle w="100%">
        {faqData?.map((faq: any, idx: number) => (
          <AccordionItem
            key={idx}
            borderTopWidth={idx === 0 ? 0 : 1}
            borderColor={isMobile ? "gray.300" : "gray.100"}
            w="100%"
          >
            <AccordionButton
              _expanded={{ fontWeight: 600 }}
              py={4}
              _hover={{ bg: "transparent" }}
            >
              <Box
                flex="1"
                textAlign="left"
                color="#2F2F34"
                fontWeight={500}
                fontSize="18px"
              >
                {faq?.question}
              </Box>
              <FaqAccordionIcon />
            </AccordionButton>

            {faq?.answer && (
              <AccordionPanel pb={4}>
                <Box
                  color="#5C6670"
                  fontWeight={400}
                  fontSize="16px"
                  mb={faq?.documents && faq.documents.length > 0 ? 3 : 0}
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  <LinkifyText text={faq?.answer} />
                </Box>
                {faq?.documents && faq.documents.length > 0 && (
                  <Flex
                    justifyContent="flex-end"
                    mt={3}
                    alignItems="center"
                    gap={2}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="#1819B4"
                      color="#1819B4"
                      fontWeight={600}
                      fontSize="12px"
                      leftIcon={
                        isDownloadingFaq(faq?.id) ? (
                          <Spinner size="xs" color="#1819B4" />
                        ) : (
                          <DownloadIcon boxSize={3} color="#1819B4" />
                        )
                      }
                      onClick={async () => {
                        const documentIds = faq.documents.map(
                          (doc: any) => doc.id
                        );
                        const sanitizedTitle =
                          faq.question
                            ?.replace(/[^a-z0-9]/gi, "_")
                            .toLowerCase() || "faq_files";
                        await downloadDocumentsZip(
                          documentIds,
                          faq?.id,
                          `${sanitizedTitle}.zip`
                        );
                      }}
                      borderRadius="8px"
                      height="28px"
                      px="12px"
                      py="4px"
                      _hover={{ bg: "#F5F7FA" }}
                      isDisabled={isDownloadingFaq(faq?.id)}
                    >
                      {isDownloadingFaq(faq?.id)
                        ? t("faq.downloading")
                        : t("faq.downloadFiles")}
                    </Button>
                  </Flex>
                )}
              </AccordionPanel>
            )}
          </AccordionItem>
        ))}
      </Accordion>
      {/* Pagination */}
      {pageCount > 0 && (
        <Box mt={6} display="flex" justifyContent="center">
          <Paginate
            marginTop={0}
            page={pageNo}
            count={pageCount}
            pageSize={pageSize || RECORDS_PER_PAGE}
            onPageChange={handlePageChange}
            variant="ghost"
          />
        </Box>
      )}
    </Box>
  );
};

export default FaqTab;
